<template>
  <div class="mx-auto  p-4 select-none"
       @click="onUserTap" @touchstart.prevent="onUserTap">
    
    <!-- Úvod -->
    <div v-if="phase==='ready'">
      <p>
        Vigilancia označuje schopnosť dlhodobo udržať pozornosť pri monotónnych, nízko-stimulujúcich podnetoch a spoľahlivo detegovať zriedkavé ciele.
        v náhodných intervaloch. Vigilancia dopĺňa dotazníkové a pamäťové merania o objektívnu, časovo citlivú metriku bdelosti a udržania výkonu. V edukačnom kontexte pomáha odhadnúť, ako študenti zvládajú dlhšie, monotónne alebo striedmo stimulujúce pasáže (napr. čítanie, opakované cvičenia), a poskytuje signály pre voľbu dĺžky blokov, frekvenciu prestávok či rytmus striedania aktivít.
        </p><p>Test trvá {{ durationSec }} s. Počas testu nie je možné prerušenie; ak stránku opustíte, zaznamená sa dovtedajšie trvanie a priebežné výsledky.</p>
      <div class="text-center mb-4">
        <div class="text-center">
          <div class="text-left mb-3">
            <label class="inline-flex items-start gap-2">
              <input type="checkbox" v-model="agreement" :true-value="1" :false-value="0" />
              <span class="text-xs opacity-80">
                Súhlasím s anonymným spracovaním mojich odpovedí na vedecké účely.
              </span>
            </label>
          </div>
          <v-btn color="primary" @click.stop="start" :disabled="agreement !== 1">Spustiť</v-btn>
        </div>
      </div>
    </div>

    <!-- Priebeh (bez progress baru) -->
    <div v-else-if="phase==='running'" class="mb-6">
      <div class="text-xs mt-1 opacity-80">Čas: {{ elapsedSec }} s &middot; Reakcií: {{ rts.length }}</div>

      <div class="mt-6">
        <!-- Stavový text -->
        <div class="text-sm opacity-80 mb-3 min-h-[1.25rem]">
          <span v-if="awaitingStimulus && !isHolding">Čakajte…</span>
          <span v-else-if="stimulusOn">Podnet! Ťuknite teraz</span>
          <span v-else-if="isHolding">Reakčný čas</span>
        </div>

        <!-- „Chlievik“ s milisekundami -->
        <div class="h-44 flex items-center justify-center">
          <div class="w-48 h-48 rounded-2xl flex items-center justify-center"
               :style="boxStyle">
            <span class="pvt-ms text-4xl font-semibold tabular-nums select-none">
              <!-- Zobrazenie ms: počas podnetu živé ms, po kliknutí zamrznutý čas -->
              <template v-if="stimulusOn || isHolding">{{ displayMsText }}</template>
              <template v-else> </template>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Výsledky -->
    <div v-else-if="phase==='finished'" class="mt-4 text-left">
      <v-card outlined class="pa-3 mt-4">
        <h3 class="font-semibold mb-2">Výsledky</h3>
        <ul class="text-sm mb-3">
          <li><strong>Trvanie (s):</strong> {{ durationSecActual }}</li>
          <li><strong>Počet reakcií:</strong> {{ rts.length }}</li>
          <li><strong>Medián RT (ms):</strong> {{ medianRT===null ? '—' : Math.round(medianRT) }}</li>
          <li><strong>Priemer RT (ms):</strong> {{ meanRT===null ? '—' : Math.round(meanRT) }}</li>
          <li><strong>Lapses (≥ {{ lapseMs }} ms):</strong> {{ lapses }}</li>
          <li><strong>False starts (&lt; {{ minRTms }} ms):</strong> {{ falseStarts }}</li>
        </ul>
        <div class="d-flex justify-end">
          <v-btn color="primary" type="submit" class="mr-2" @click="closeTest">Koniec</v-btn>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import axios from '../../axios-auth.js'

export default {
  name: "PVTBrief",
  props: {
    durationSecProp: { type: Number, default: 180 }, // 3 min
    isiMinSec:       { type: Number, default: 2 },
    isiMaxSec:       { type: Number, default: 10 },
    beaconUrl:       { type: String, default: "" },
    participantId:   { type: [String, Number], default: "" },
    minRTms:         { type: Number, default: 100 },  // <100 ms = false start
    lapseMs:         { type: Number, default: 500 },  // ≥500 ms = lapse
    holdMs:          { type: Number, default: 600 }   // po kliknutí nechaj zobrazený RT
  },
  data() {
    return {
      // stav
      phase: 'ready', // 'ready' | 'running' | 'finished'
      durationSec: this.durationSecProp,
      startTime: null,
      elapsedSec: 0,
      progressTimer: null,
      finalizeGuard: false,
      startTime: null,
      myStartTime: null,
      agreement: 0,

      // stimulácia
      awaitingStimulus: true,
      stimulusOn: false,
      stimTimeout: null,
      stimOnAt: null,      // performance.now() pri zapnutí podnetu
      stimOnTimes: [],     // ms od štartu (log)

      // dáta
      rts: [],
      falseStarts: 0,
      falseStartTimes: [],

      // vizuál / feedback
      boxColor: '#fafafa',
      boxBorder: '#ddd',
      feedbackTimer: null,

      // zobrazenie ms v chlieviku
      displayMs: 0,
      rafId: null,
      holdUntil: null,     // performance.now() do kedy držať zobrazený RT po kliknutí
    };
  },
  computed: {
    durationMsActual() {
      return this.startTime ? Math.max(0, performance.now() - this.startTime) : 0;
    },
    durationSecActual() {
      return Math.round(this.durationMsActual / 1000);
    },
    medianRT() {
      if (!this.rts.length) return null;
      const arr = this.rts.slice().sort((a,b)=>a-b);
      const mid = Math.floor(arr.length / 2);
      return arr.length % 2 ? arr[mid] : (arr[mid-1]+arr[mid])/2;
    },
    meanRT() {
      if (!this.rts.length) return null;
      return this.rts.reduce((a,b)=>a+b,0) / this.rts.length;
    },
    lapses() {
      return this.rts.filter(v => v >= this.lapseMs).length;
    },
    isHolding() {
      return this.holdUntil !== null && performance.now() < this.holdUntil;
    },
    boxStyle() {
      return {
        backgroundColor: this.boxColor,
        border: `3px solid ${this.boxBorder}`
      };
    },
    displayMsText() {
      // zobrazuj celé milisekundy
      return Math.max(0, Math.round(this.displayMs)).toString();
    }
  },
  mounted() {
    this.startTime = Date.now()
  },
  methods: {
    start() {
      this.resetState();
      this.phase = 'running';
      this.startTime = performance.now();
      this.myStartTime = Date.now()
      // sekundové „tikanie“ kvôli zobrazeniu elapsedSec a koncu testu
      this.progressTimer = setInterval(() => {
        this.elapsedSec++;
        if (this.elapsedSec >= this.durationSec) {
          this.finishAndFinalize(true);
        }
      }, 1000);

      this.scheduleNextStimulus();

      // odchody/unload
      window.addEventListener('beforeunload', this.onBeforeUnload, true);
      window.addEventListener('pagehide', this.onPageHide, true);
    },
    resetState() {
      this.elapsedSec = 0;
      this.awaitingStimulus = true;
      this.stimulusOn = false;
      this.stimOnAt = null;
      this.stimOnTimes = [];
      this.rts = [];
      this.falseStarts = 0;
      this.falseStartTimes = [];
      this.finalizeGuard = false;

      this.setBoxNeutral();

      // stop tickery a časovače
      if (this.feedbackTimer) { clearTimeout(this.feedbackTimer); this.feedbackTimer = null; }
      if (this.progressTimer) { clearInterval(this.progressTimer); this.progressTimer = null; }
      if (this.stimTimeout)   { clearTimeout(this.stimTimeout);   this.stimTimeout = null; }
      if (this.rafId)         { cancelAnimationFrame(this.rafId); this.rafId = null; }

      // ms displej
      this.displayMs = 0;
      this.holdUntil = null;
    },
    setBoxNeutral() {
      this.boxColor  = '#fafafa';
      this.boxBorder = '#ddd';
    },
    setBoxGreen() {
      this.boxColor  = '#e8f5e9';
      this.boxBorder = '#2e7d32';
    },
    setBoxRed() {
      this.boxColor  = '#ffebee';
      this.boxBorder = '#c62828';
    },
    scheduleNextStimulus() {
      const isi = this.randomISI();
      this.awaitingStimulus = true;
      this.stimulusOn = false;
      this.holdUntil = null; // ukonči prípadné držanie starého RT
      this.setBoxNeutral();
      if (this.stimTimeout) clearTimeout(this.stimTimeout);
      this.stimTimeout = setTimeout(() => {
        // zapni podnet
        this.awaitingStimulus = false;
        this.stimulusOn = true;
        this.stimOnAt = performance.now();
        this.displayMs = 0;         // reset zobrazovača
        this.startMsTicker();       // živé ms počas podnetu
        const tFromStart = Math.round(this.stimOnAt - this.startTime);
        this.stimOnTimes.push(tFromStart);
      }, isi * 1000);
    },
    randomISI() {
      const min = Math.max(0.5, this.isiMinSec);
      const max = Math.max(min, this.isiMaxSec);
      return Math.random() * (max - min) + min;
    },
    startMsTicker() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      const tick = () => {
        if (!this.stimulusOn || !this.stimOnAt) { this.rafId = null; return; }
        this.displayMs = performance.now() - this.stimOnAt;
        this.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    },
    stopMsTicker() {
      if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null; }
    },
    onUserTap() {
      if (this.phase !== 'running') return;

      const now = performance.now();
      const tFromStart = Math.round(now - this.startTime);

      if (this.stimulusOn && this.stimOnAt) {
        // platná reakcia alebo veľmi rýchla (false start) počas podnetu
        const rt = now - this.stimOnAt;
        if (rt < this.minRTms) {
          // príliš rýchle -> false start, podnet necháme bežať ďalej
          this.falseStarts += 1;
          this.falseStartTimes.push(tFromStart);
          this.flashBox('red', 250);
          return;
        }
        // zapíš RT, vypni podnet, zamraz čas v chlieviku na holdMs
        this.stopMsTicker();
        this.displayMs = Math.round(rt);
        this.rts.push(rt);
        this.stimulusOn = false;
        this.stimOnAt = null;

        this.setBoxGreen();
        this.holdUntil = performance.now() + this.holdMs;

        // po uplynutí holdMs naplánuj ďalší podnet
        setTimeout(() => {
          // ak medzitým neskončil test / stránka neodišla
          if (this.phase === 'running' && !this.stimulusOn) {
            this.scheduleNextStimulus();
          }
        }, this.holdMs);
      } else {
        // klik mimo podnetu -> false start
        this.falseStarts += 1;
        this.falseStartTimes.push(tFromStart);
        this.flashBox('red', 250);
      }
    },
    flashBox(kind, ms = 300) {
      if (this.feedbackTimer) { clearTimeout(this.feedbackTimer); this.feedbackTimer = null; }
      if (kind === 'green') this.setBoxGreen();
      else this.setBoxRed();
      this.feedbackTimer = setTimeout(() => {
        // ak práve nedržíme zobrazený RT po platnej reakcii, vráť neutrál
        if (!this.isHolding && !this.stimulusOn) this.setBoxNeutral();
        this.feedbackTimer = null;
      }, ms);
    },
    finishAndFinalize(completed) {
      if (this.finalizeGuard) return;
      this.finalizeGuard = true;

      if (this.progressTimer) { clearInterval(this.progressTimer); this.progressTimer = null; }
      if (this.stimTimeout)   { clearTimeout(this.stimTimeout);   this.stimTimeout   = null; }
      this.stopMsTicker();

      this.phase = 'finished';
      const summary = this.buildSummary(completed ? 'completed' : 'abandon');
      this.emitAndBeacon(summary);

      window.removeEventListener('beforeunload', this.onBeforeUnload, true);
      window.removeEventListener('pagehide', this.onPageHide, true);
    },
    buildSummary(endReason) {
      this.writeAnswer(endReason)
      return {
        participant_id: this.participantId || null,
        end_reason: endReason,                       // 'completed' | 'abandon'
        duration_sec: this.durationSecActual,
        planned_duration_sec: this.durationSec,
        trials: this.rts.length,
        median_rt_ms: this.medianRT === null ? null : Math.round(this.medianRT),
        mean_rt_ms: this.meanRT === null ? null : Math.round(this.meanRT),
        lapses: this.lapses,
        false_starts: this.falseStarts,
        params: {
          isi_min_sec: this.isiMinSec,
          isi_max_sec: this.isiMaxSec,
          min_rt_ms: this.minRTms,
          lapse_ms: this.lapseMs,
          hold_ms: this.holdMs
        },
        timestamps: {
          rts_ms: this.rts.slice(),
          stim_on_ms: this.stimOnTimes.slice(),
          false_starts_ms: this.falseStartTimes.slice()
        },
        client_time_iso: new Date().toISOString()
      };
    },
    emitAndBeacon(summary) {
      this.$emit('finalize', summary);
      try { localStorage.setItem('pvtb_summary', JSON.stringify(summary)); } catch(e) {}

      if (this.beaconUrl) {
        try {
          const blob = new Blob([JSON.stringify(summary)], { type: 'application/json' });
          navigator.sendBeacon(this.beaconUrl, blob);
        } catch(e) { /* no-op */ }
      }
    },
    onBeforeUnload() {
      if (this.phase === 'running' && !this.finalizeGuard) {
        this.finishAndFinalize(false);
      }
    },
    onPageHide() {
      if (this.phase === 'running' && !this.finalizeGuard) {
        this.finishAndFinalize(false);
      }
    },
    restart() {
      this.resetState();
      this.phase = 'ready';
      window.removeEventListener('beforeunload', this.onBeforeUnload, true);
      window.removeEventListener('pagehide', this.onPageHide, true);
    },
    writeAnswer(_final) {
      const _timestamps = {
          rts_ms: this.rts.slice(),
          stim_on_ms: this.stimOnTimes.slice(),
          false_starts_ms: this.falseStartTimes.slice()
      }
      const _score = Math.round((this.medianRT ? (10000 / (this.medianRT + this.falseStarts)) : 0 )*100)/100
      const _data = {
        id: 0,
        test_id: this.$route.params.id,
        score: _score,
        answers : JSON.stringify(_timestamps),
        final: _final = 'completed' ? 1 : 0,
        rslt: '' + this.medianRT + ' / ' + this.meanRT + ', ' + ('miss: ' + this.falseStarts) ,
        agreement: this.agreement,
        time_length: this.myStartTime ? Math.round((Date.now() - this.myStartTime)/1000) : 0
      } 
      axios.post('/write-my-person-test-answer', _data, this.$store.getters.getAxiosConfig)
      .then(res => {
          // this.answer_id = res.data.answer_id
      })
      .catch(error => {
          this.$store.commit('setFeedback', error)
      })
    },
    closeTest() {
      this.$router.push({name:'personalisation_tests', params: {}}) 
    },
  },
  beforeUnmount() {
    if (this.progressTimer) clearInterval(this.progressTimer);
    if (this.stimTimeout) clearTimeout(this.stimTimeout);
    if (this.rafId) cancelAnimationFrame(this.rafId);
    window.removeEventListener('beforeunload', this.onBeforeUnload, true);
    window.removeEventListener('pagehide', this.onPageHide, true);
  }
};
</script>

<style scoped>
/* jednoduché utility (ak nepoužívate Tailwind) */
.max-w-xl { max-width: 36rem; }
.p-4 { padding: 1rem; }
.mx-auto { margin-left: auto; margin-right: auto; }
.mb-2 { margin-bottom: .5rem; }
.mb-3 { margin-bottom: .75rem; }
.mt-1 { margin-top: .25rem; }
.mt-4 { margin-top: 1rem; }
.mt-6 { margin-top: 1.5rem; }
.text-center { text-align: center; }
.text-left { text-align: left; }
.text-xl { font-size: 1.25rem; line-height: 1.75rem; }
.font-semibold { font-weight: 600; }
.flex { display: flex; }
.items-center { align-items: center; }
.justify-center { justify-content: center; }
.cursor-pointer { cursor: pointer; }
.select-none { user-select: none; }
/* helper pre min výšku riadku so stavovým textom */
.min-h-\[1\.25rem\] { min-height: 1.25rem; }
.h-44 { height: 11rem; }
.w-48 { width: 12rem; }
.rounded-2xl { border-radius: 1rem; }
.tabular-nums { font-variant-numeric: tabular-nums; }
.text-4xl { font-size: 2.25rem; line-height: 2.5rem; }
.pvt-ms { color: #111 !important; }
</style>
